/**
 * organizeStyles — the dark-rail chrome for the organization surface
 * (2026-09-09): anchored menus in the SidebarAccountMenu idiom, ghost icon
 * buttons in the RoutineManageButton idiom, the inline rename input, the
 * in-list notice, and the drop-target look. Every colour is the rail's own
 * gold family; the menus ride BODY_PORTAL_Z_INDEX like every body portal.
 */
import styled from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './styles';

export const RailMenu = styled.div.attrs<{ $left: number; $top: number }>((props) => ({
  style: { left: `${props.$left}px`, top: `${props.$top}px` },
}))<{ $left: number; $top: number }>`
  position: fixed;
  z-index: ${BODY_PORTAL_Z_INDEX};
  min-width: 208px;
  max-width: min(280px, calc(100vw - 16px));
  padding: 6px;
  background: rgba(26, 26, 26, 0.98);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(198, 167, 94, 0.12);
`;

export const RailMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.$danger ? 'rgba(226, 120, 120, 0.95)' : 'var(--brand-gold)')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  &:hover:not(:disabled) {
    background: rgba(198, 167, 94, 0.1);
  }
  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: -2px;
  }
  svg {
    flex: none;
    width: 14px;
    height: 14px;
  }
`;

export const RailMenuHeading = styled.div`
  padding: 6px 10px 4px;
  color: rgba(182, 146, 76, 0.75);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

export const RailMenuInput = styled.input`
  width: 100%;
  margin: 2px 0 4px;
  padding: 7px 10px;
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 6px;
  background: rgba(198, 167, 94, 0.06);
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(182, 146, 76, 0.55);
  }
  &:focus {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.2);
  }
`;

/**
 * A quiet ghost icon button for the rail (the RoutineManageButton language):
 * transparent at rest, revealed by the row / header it lives in, always
 * visible on touch, a real hit size.
 */
export const RailGhostIconButton = styled.button<{ $always?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: rgba(182, 146, 76, 0.8);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  opacity: ${(props) => (props.$always ? 1 : 0)};
  transition: opacity 0.12s ease, background 0.12s ease;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.1);
  }
  &:focus-visible {
    opacity: 1;
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
  svg {
    width: 12px;
    height: 12px;
    display: block;
  }

  /* Touch: no hover exists — the affordance is always there, at a real size. */
  @media (hover: none) {
    opacity: 1;
    width: 28px;
    height: 28px;
  }
`;

/** The inline rename field a row's title swaps for (dark twin of ThreadHeaderRenameInput). */
export const RailInlineInput = styled.input`
  width: 100%;
  min-width: 0;
  padding: 2px 6px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--brand-gold-hover);
  background: rgba(198, 167, 94, 0.06);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 4px;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: rgba(198, 167, 94, 0.6);
  }
`;

/** The in-list notice ("moved to clients · undo"), pinned under the list. */
export const RailNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 8px 4px;
  padding: 8px 10px;
  background: rgba(198, 167, 94, 0.1);
  border: 1px solid rgba(198, 167, 94, 0.3);
  border-radius: 8px;
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  line-height: 1.3;
`;

export const RailNoticeAction = styled.button`
  margin-left: auto;
  padding: 2px 8px;
  background: transparent;
  border: 1px solid rgba(198, 167, 94, 0.45);
  border-radius: 6px;
  color: var(--brand-gold);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    background: rgba(198, 167, 94, 0.12);
  }
  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
`;
